/**
 * Visumo(hacobune) SNAP詳細モーダル内の「関連アイテム」商品タイルに
 * バリエーション選択セレクト＋「カートに入れる」ボタンを後付け注入する。
 *
 * 対象DOM: .hacobune-insta-item-tile-product（モーダル右ペインの商品タイル）
 * 監視方式: MutationObserver（モーダル開閉・再描画で出現するタイルを検知）
 * カート追加: POST /cart/add.js → variant:added / cart:refresh を発火してミニカートを開く
 *
 * バリアントIDは Visumo の商品コードに頼らず、商品リンクの handle から
 * Shopify Ajax API `/products/{handle}.js` で取り直す（フィード切替後に自動整合）。
 * ------------------------------------------------------------------
 * ベース: BAKE案件 visumo-cart-extension（旧ecbn線）を hacobune 線へ移植。
 * Visumo のアップデートでクラス名が変わった場合は SELECTOR 定数だけ直す。
 */
(() => {
  'use strict';

  const SELECTOR = {
    // 商品タイル：モーダル版＋詳細ページ（page-mode）版＋SPスライダー版の各DOMに対応
    item: '.hacobune-insta-item-tile-product, .hacobune-simple-related-product-list__item, .hacobune-simple-related-product-list__slider-item',
    // タイル内の商品リンク（href から handle を取る）
    itemLink: 'a[href]',
    // Visumo が商品データを載せている属性（JSON文字列）
    dataProduct: 'data-product',
    // タイル内の表示サイズ／カラー（初期選択に使う）：両DOM対応
    size: '.related-product-item-size, .hacobune-simple-related-product-list__size',
    color: '.related-product-item-color, .hacobune-simple-related-product-list__color',
    injectedFlag: 'data-vsm-cart-injected',
    miniCart: '#mini-cart',
  };

  const CLASS = {
    root: 'c-vsm-cart',
    select: 'c-vsm-cart__select',
    trigger: 'c-vsm-cart__trigger',
    options: 'c-vsm-cart__options',
    option: 'c-vsm-cart__option',
    optionSelected: 'is-selected',
    optionDisabled: 'is-disabled',
    openState: 'is-open',
    btn: 'c-vsm-cart__btn',
    error: 'c-vsm-cart__error',
  };

  const LABEL = {
    add: 'カートに入れる',
    soldOut: '売り切れ',
    adding: '追加中...',
    added: '追加しました ✓',
    networkError: '通信エラー',
    genericError: 'カート追加に失敗',
  };

  /** @type {Map<string, object>} 商品handle→product.jsキャッシュ */
  const productCache = new Map();

  /**
   * body直下にマウントした matrix ホストの管理台帳（itemEl → host）。
   * Visumoの再描画で元タイルがDOMから外れたら、対応するホストも削除して
   * 古いDOM・イベントが溜まらないようにする。
   * @type {Map<Element, Element>}
   */
  const matrixHosts = new Map();

  /** 元タイルが外れた matrix ホストを掃除する */
  const cleanupOrphanHosts = () => {
    matrixHosts.forEach((host, itemEl) => {
      if (!itemEl.isConnected) {
        host.remove();
        matrixHosts.delete(itemEl);
      }
    });
  };

  /**
   * 商品ページURLからhandleを抽出。
   * Shopify: /products/{handle} → handle。旧duffer.jp形式はここでは解決できない（フィード切替後に有効）。
   * @param {string} href
   * @returns {string|null}
   */
  const extractHandle = (href) => {
    if (!href) return null;
    const match = href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  };

  /**
   * タイルの data-product JSON を安全にパース
   * @param {Element} link
   * @returns {object|null}
   */
  const parseDataProduct = (link) => {
    const raw = link?.getAttribute(SELECTOR.dataProduct);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  };

  /**
   * Shopify Ajax APIから商品データ取得（キャッシュ付き）
   * @param {string} handle
   * @returns {Promise<object|null>}
   */
  const fetchProduct = async (handle) => {
    if (productCache.has(handle)) return productCache.get(handle);
    try {
      const res = await fetch(`/products/${handle}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`product fetch failed: ${res.status}`);
      const data = await res.json();
      productCache.set(handle, data);
      return data;
    } catch (e) {
      // フィード未切替（旧duffer.jp handle）だと 404 になる。想定内なので握りつぶす。
      productCache.set(handle, null);
      return null;
    }
  };

  /**
   * バリアントタイトルを「選択肢1 / 選択肢2」形式に整形
   * @param {object} variant
   * @returns {string}
   */
  const variantLabel = (variant) => {
    if (variant.title && variant.title !== 'Default Title') return variant.title;
    return Array.isArray(variant.options) ? variant.options.join(' / ') : 'デフォルト';
  };

  /**
   * タイルの表示サイズ／カラーに一致する variant.id を推定（初期選択用）。
   * Visumo が出しているサイズ/カラーと同じものを初期選択にして、
   * ユーザーが見たままの1個をそのまま追加できるようにする。
   * @param {Element} itemEl
   * @param {object[]} variants
   * @returns {string|null}
   */
  const guessPreferredVariantId = (itemEl, variants) => {
    const norm = (s) => (s || '').toString().trim().toLowerCase();
    const size = norm(itemEl.querySelector(SELECTOR.size)?.textContent);
    const color = norm(itemEl.querySelector(SELECTOR.color)?.textContent);
    if (!size && !color) return null;

    const match = variants.find((v) => {
      const opts = (v.options || []).map(norm);
      const okSize = !size || opts.some((o) => o === size || o.includes(size));
      const okColor = !color || opts.some((o) => o === color || o.includes(color));
      return okSize && okColor;
    });
    return match ? String(match.id) : null;
  };

  /**
   * カート追加API呼び出し
   * @param {number} variantId
   * @returns {Promise<{ok:boolean, item?:object, error?:string}>}
   */
  const addToCart = async (variantId) => {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const data = await res.json();
      if (!res.ok) return { ok: false, error: data.description || LABEL.genericError };
      return { ok: true, item: data };
    } catch (e) {
      console.error('[c-vsm-cart] add error:', e);
      return { ok: false, error: LABEL.networkError };
    }
  };

  /**
   * テーマ既存のカート更新フローに合流（Focal規約）
   * @param {object} addedItem
   */
  const notifyCartUpdate = (addedItem) => {
    const root = document.documentElement;
    root.dispatchEvent(
      new CustomEvent('variant:added', {
        bubbles: true,
        detail: { variant: addedItem?.items?.[0] || addedItem },
      })
    );
    root.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    const miniCart = document.querySelector(SELECTOR.miniCart);
    if (miniCart && 'open' in miniCart) {
      miniCart.open = true;
    }
  };

  /**
   * 単一バリアント用のボタンを生成
   * @param {object} variant
   * @returns {HTMLButtonElement}
   */
  const createSingleVariantButton = (variant) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = CLASS.btn;
    btn.dataset.variantId = String(variant.id);
    btn.textContent = variant.available ? LABEL.add : LABEL.soldOut;
    if (!variant.available) btn.disabled = true;
    return btn;
  };

  /**
   * カスタムドロップダウンを生成。
   * オプションリストは document.body 直下に position:fixed でマウントし、
   * Visumoモーダルの overflow:hidden / transform に隠されないようにする。
   * @param {object[]} variants
   * @param {string} [preferredVariantId]
   * @returns {HTMLElement} value getter付きコンテナ
   */
  const createVariantSelect = (variants, preferredVariantId) => {
    const container = document.createElement('div');
    container.className = CLASS.select;

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = CLASS.trigger;
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    const list = document.createElement('ul');
    list.className = CLASS.options;
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    const preferred = preferredVariantId && variants.find(
      (v) => String(v.id) === String(preferredVariantId) && v.available
    );
    const firstAvailable = variants.find((v) => v.available);
    let selectedId = String((preferred || firstAvailable || variants[0]).id);

    const updateTrigger = () => {
      const v = variants.find((x) => String(x.id) === selectedId);
      trigger.textContent = v ? variantLabel(v) + (v.available ? '' : '（売り切れ）') : '';
    };

    variants.forEach((v) => {
      const opt = document.createElement('li');
      opt.className = CLASS.option;
      opt.setAttribute('role', 'option');
      opt.dataset.value = String(v.id);
      opt.textContent = variantLabel(v) + (v.available ? '' : '（売り切れ）');
      if (!v.available) opt.classList.add(CLASS.optionDisabled);
      if (String(v.id) === selectedId) opt.classList.add(CLASS.optionSelected);

      opt.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!v.available) return;
        selectedId = String(v.id);
        list.querySelectorAll('.' + CLASS.option).forEach((el) => {
          el.classList.toggle(CLASS.optionSelected, el.dataset.value === selectedId);
        });
        updateTrigger();
        closeDropdown();
        container.dispatchEvent(new CustomEvent('change', { bubbles: false }));
      });
      list.appendChild(opt);
    });

    const positionList = () => {
      const rect = trigger.getBoundingClientRect();
      const listHeight = list.offsetHeight || 200;
      const viewportH = window.innerHeight;
      const showAbove = rect.bottom + listHeight > viewportH && rect.top > listHeight;
      list.style.left = rect.left + 'px';
      list.style.width = rect.width + 'px';
      list.style.top = showAbove ? (rect.top - listHeight - 2) + 'px' : (rect.bottom + 2) + 'px';
    };

    const onScrollOrResize = () => {
      if (!list.hidden) positionList();
    };

    const openDropdown = () => {
      list.hidden = false;
      container.classList.add(CLASS.openState);
      trigger.setAttribute('aria-expanded', 'true');
      positionList();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
    };
    const closeDropdown = () => {
      list.hidden = true;
      container.classList.remove(CLASS.openState);
      trigger.setAttribute('aria-expanded', 'false');
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (list.hidden) openDropdown(); else closeDropdown();
    });

    document.addEventListener('click', (e) => {
      if (list.hidden) return;
      if (container.contains(e.target) || list.contains(e.target)) return;
      closeDropdown();
    }, true);

    document.body.appendChild(list);
    container.appendChild(trigger);
    updateTrigger();

    Object.defineProperty(container, 'value', { get: () => selectedId });

    // タイル除去時に body直下の list を掃除できるよう参照を持たせる
    container._optionsList = list;

    return container;
  };

  /**
   * カートUIをDOMに描画（safe DOM only）
   * @param {Element} itemEl
   * @param {object} product
   * @param {string} [preferredVariantId]
   */
  const renderCartUI = (itemEl, product, preferredVariantId) => {
    if (!product?.variants?.length) return;

    const variants = product.variants;
    const hasAnyAvailable = variants.some((v) => v.available);
    const wrap = document.createElement('div');
    wrap.className = CLASS.root;

    // ※「商品詳細へ」リンクは先方FBにより削除（2026-08-12）。
    //   タイル本体が商品ページへのリンクのままなので、導線自体は失われない。

    // Visumoはタイル全体にデリゲートclickを張っており、放置すると商品ページへ遷移する。
    // 注入UIの bubble段階で伝播を止める（capture段階で止めると自ボタンにも届かない）。
    ['click', 'mousedown', 'mouseup', 'touchstart', 'touchend', 'pointerdown', 'pointerup']
      .forEach((evt) => {
        wrap.addEventListener(evt, (e) => e.stopPropagation(), false);
      });

    if (variants.length === 1) {
      wrap.appendChild(createSingleVariantButton(variants[0]));
    } else {
      const select = createVariantSelect(variants, preferredVariantId);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = CLASS.btn;
      btn.textContent = hasAnyAvailable ? LABEL.add : LABEL.soldOut;
      if (!hasAnyAvailable) {
        btn.disabled = true;
        const trigger = select.querySelector('.' + CLASS.trigger);
        if (trigger) trigger.disabled = true;
      }
      wrap.appendChild(select);
      wrap.appendChild(btn);
    }

    const error = document.createElement('p');
    error.className = CLASS.error;
    error.hidden = true;
    wrap.appendChild(error);

    bindHandler(wrap);
    itemEl.appendChild(wrap);
  };

  /**
   * ボタンクリックのハンドラ
   * @param {Element} wrap
   */
  const bindHandler = (wrap) => {
    const btn = wrap.querySelector('.' + CLASS.btn);
    const select = wrap.querySelector('.' + CLASS.select);
    const errorEl = wrap.querySelector('.' + CLASS.error);
    if (!btn) return;

    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const variantId = Number(select?.value || btn.dataset.variantId);
      if (!variantId) return;

      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = LABEL.adding;
      errorEl.hidden = true;

      const result = await addToCart(variantId);

      if (result.ok) {
        btn.textContent = LABEL.added;
        notifyCartUpdate(result.item);
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      } else {
        btn.textContent = originalText;
        btn.disabled = false;
        errorEl.textContent = result.error || LABEL.genericError;
        errorEl.hidden = false;
      }
    });
  };

  /**
   * 旧duffer.jp URL（/products/{商品コード}?sku={フルSKU}）から joix-df の
   * handle 命名規則で候補を組み立てて引き当てる。フィード未切替の間のフォールバック。
   * 実データの命名規則: {商品コード小文字}-sku-{フルSKU小文字}
   *   例) 2679075100ct044___-sku-2679075100ct044___120m
   * ※ /search/suggest.json はこのストアで 417 (Unsupported buyer locale) を返すため使わない。
   * @param {string} href
   * @returns {Promise<object|null>}
   */
  const fetchProductBySku = async (href) => {
    if (!href) return null;
    let sku = null;
    let code = null;
    try {
      const url = new URL(href, location.origin);
      sku = url.searchParams.get('sku');
      code = extractHandle(url.pathname);
    } catch (_) { /* noop */ }
    if (!sku && !code) return null;

    const candidates = [];
    if (code && sku) candidates.push(`${code.toLowerCase()}-sku-${sku.toLowerCase()}`);
    if (code) candidates.push(code.toLowerCase());
    if (sku) candidates.push(sku.toLowerCase());

    for (const handle of candidates) {
      const product = await fetchProduct(handle);
      if (product) return product;
    }
    return null;
  };

  /**
   * 単一の商品タイルに対してUIを注入
   * @param {Element} itemEl
   */
  const injectInto = async (itemEl) => {
    if (itemEl.hasAttribute(SELECTOR.injectedFlag)) return;
    const link = itemEl.querySelector(SELECTOR.itemLink);
    const href = link?.getAttribute('href');
    const handle = extractHandle(href);
    if (!handle) return;

    itemEl.setAttribute(SELECTOR.injectedFlag, '1');

    let product = await fetchProduct(handle);
    if (!product) {
      // フィード未切替（旧duffer.jp handle）→ SKU予測検索で引き当てを試す
      product = await fetchProductBySku(href);
    }
    if (!product) {
      // それでも取得不可。フラグは残して再試行ループを防ぐ。
      return;
    }
    if (itemEl.querySelector('.' + CLASS.root)) return;

    // 商品を引き当てられた場合は、旧duffer.jp向きのリンクを当ストアの商品ページに書き換える
    // （Visumo側の商品データ差し替えを待たずに正しい遷移にする。別タブ遷移も解除）
    if (product.url && link) {
      link.setAttribute('href', product.url);
      link.removeAttribute('target');
      link.removeAttribute('rel');
    }

    // コーデ詳細と同じ c-variant-matrix モーダル（?view=vsm-matrix で取得）を注入。
    // 取得できない場合のみ従来の直接カートUIにフォールバック。
    const matrixHtml = await fetchMatrixHtml(product.url);
    if (matrixHtml) {
      renderMatrixUI(itemEl, product, matrixHtml);
    } else {
      const preferred = guessPreferredVariantId(itemEl, product.variants);
      renderCartUI(itemEl, product, preferred);
    }
  };

  /**
   * 商品ビュー product.vsm-matrix（c-variant-matrix / force_modal のみ）を取得
   * @param {string} productUrl
   * @returns {Promise<string|null>}
   */
  const fetchMatrixHtml = async (productUrl) => {
    try {
      const res = await fetch(`${productUrl}?view=vsm-matrix`, { headers: { Accept: 'text/html' } });
      if (!res.ok) return null;
      const html = await res.text();
      if (!html.includes('variant-matrix')) return null;
      return html;
    } catch (_) {
      return null;
    }
  };

  /**
   * c-variant-matrix（選択モーダル）を注入。
   * Visumoは .hacobune-app-container * { padding:0; margin:0 } の全称リセットを持つため、
   * matrix本体は body 直下（リセットの射程外）にマウントし、行内には代理の
   * 「カートに入れる」ボタンだけ置いて matrix のトリガーを踏む。
   * <variant-matrix> はカスタム要素なので挿入時に自動初期化される。
   * @param {Element} itemEl
   * @param {object} product
   * @param {string} matrixHtml
   */
  const renderMatrixUI = (itemEl, product, matrixHtml) => {
    const wrap = document.createElement('div');
    wrap.className = CLASS.root;

    // Visumoのカードデリゲートclickで商品ページへ遷移しないよう伝播を止める
    ['click', 'mousedown', 'mouseup', 'touchstart', 'touchend', 'pointerdown', 'pointerup']
      .forEach((evt) => {
        wrap.addEventListener(evt, (e) => e.stopPropagation(), false);
      });

    // ※「商品詳細へ」リンクは先方FBにより削除（2026-08-12）

    // matrix本体は body 直下へ（自前トリガーはCSSで非表示にする）。
    // itemEl と紐付けて台帳に登録し、元タイルが外れたら cleanupOrphanHosts が削除する
    const host = document.createElement('div');
    host.className = 'c-vsm-matrix-host';
    host.innerHTML = matrixHtml;
    document.body.appendChild(host);
    matrixHosts.set(itemEl, host);

    // 行内の代理トリガー
    const proxy = document.createElement('button');
    proxy.type = 'button';
    proxy.className = CLASS.btn;
    proxy.textContent = LABEL.add;
    proxy.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      host.querySelector('[data-vm-open]')?.click();
    });
    wrap.appendChild(proxy);

    itemEl.appendChild(wrap);
  };

  /**
   * 未注入の商品タイルを全件スキャンして注入
   */
  const scanAndInject = () => {
    document.querySelectorAll(SELECTOR.item).forEach((el) => {
      if (!el.hasAttribute(SELECTOR.injectedFlag)) injectInto(el);
    });
  };

  /**
   * 初期化：MutationObserver でモーダル出現・再描画を吸収
   *
   * このJSは全ページで読み込まれるため、Visumo の埋め込みが無いページでは
   * 監視を開始せずに終了する（body 全体の subtree 監視が常時走ると、
   * ヘッダーやミニカート等の通常のDOM更新のたびに全体走査が発生するため）。
   * モーダルは body 直下に生成されるので、監視対象自体は body のままにする。
   */
  const VISUMO_ROOT = '.hacobune-container';

  const init = () => {
    if (!document.querySelector(VISUMO_ROOT)) return;

    const observer = new MutationObserver(() => {
      cleanupOrphanHosts();
      scanAndInject();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    scanAndInject();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
